import { useEffect, useId, useImperativeHandle, useRef, type ReactNode, type Ref } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "@/components/design/Button";

// One step of the tutorial's guide: the assistant tour's info card, but the game has no assistant
// to say anything, so the card carries the whole explanation and the step's buttons. Its look is the tour card's
// (index.css, .grow-guide-card). `pointsUp` adds the arrow to the element right above it that the step explains.
//
// The way on takes the focus as soon as it can be used, unless the player is already somewhere else on the page, and
// is described by the card's text, so a screen reader reads the step when the focus arrives. As it takes the focus, a
// card out of view comes into view, by the least scroll that shows it whole: a keyboard player would otherwise press
// Enter, or Space to scroll on, on a button they cannot see. `ref` hands the card to the page, which brings each step
// into view.
//
// A click on the way on or the way back is where the player is, whatever held the focus before: each of the two takes
// the focus before it acts, so the card that comes next finds it on the page (with a button gone) and takes it. Safari
// gives no button the focus on a mouse click, so a follow-up that held it (the move step, spark#1236) would keep it
// and the next card would think the player is somewhere else.
//
// `onBack` adds the assistant tour's „Zurück" before the way on, so a player who went on too quickly can read the step
// before again.
//
// The text is a `div`, not a paragraph, so a step can bring its own paragraphs and lists (the inventory step's two
// lists, spark#1236); the way on's description reads all of it, lists included.

export function GuideCard({
  children,
  action,
  onAction,
  actionDisabled = false,
  onBack,
  skip,
  pointsUp = false,
  ref,
}: {
  children: ReactNode;
  action: ReactNode;
  onAction: () => void;
  actionDisabled?: boolean;
  onBack?: () => void;
  skip?: ReactNode;
  pointsUp?: boolean;
  ref?: Ref<HTMLDivElement>;
}) {
  const textId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLButtonElement>(null);
  useImperativeHandle(ref, () => cardRef.current!, []);

  useEffect(() => {
    if (actionDisabled) return;
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    actionRef.current?.focus({ preventScroll: true });
    cardRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [actionDisabled]);

  return (
    <div ref={cardRef} className={`grow-guide-card space-y-4 p-4${pointsUp ? " grow-guide-card-up" : ""}`}>
      <div id={textId} className="text-smycles-body leading-relaxed">
        {children}
      </div>
      <div className="flex flex-wrap gap-2">
        {onBack ? (
          <Button
            variant="secondary"
            size="compact"
            onClick={(event) => {
              event.currentTarget.focus({ preventScroll: true });
              onBack();
            }}
          >
            <FormattedMessage id="grow.roleplay.intro.back" defaultMessage="Back" />
          </Button>
        ) : null}
        <Button
          ref={actionRef}
          size="compact"
          disabled={actionDisabled}
          aria-describedby={textId}
          onClick={() => {
            actionRef.current?.focus({ preventScroll: true });
            onAction();
          }}
        >
          {action}
        </Button>
        {skip}
      </div>
    </div>
  );
}
